import { useEffect, useState } from 'react';
import Button from '../components/ui/Button.jsx';
import CheckIcon from '../components/ui/CheckIcon.jsx';
import Eyebrow from '../components/ui/Eyebrow.jsx';
import {
  CURRENCIES,
  PRICES_APPROVED,
  DEFAULT_CURRENCY,
  FAQ,
  INCLUDED_EVERYWHERE,
  SENSORS,
  TIERS,
  YEARLY_BILLING_ENABLED,
} from '../content/pricing.js';
import {
  formatPrice,
  guessCurrency,
  yearlyPerMonth,
  yearlySavingPercent,
  yearlyTotal,
} from '../lib/format.js';
import AccentedHeading from '../components/ui/Accent.jsx';

const STORAGE_KEY = 'chickrah:currency';

/* A picker with one option is furniture, not a control. */
const SHOW_CURRENCY_PICKER = Object.keys(CURRENCIES).length > 1;

/**
 * Pricing, inlined on the home page (it used to be its own route).
 *
 * Opens on a dark band so it reads as its own chapter between the two light
 * sections around it, with the tier cards pulled up to straddle the edge.
 */
export default function PricingSection() {
  const [yearly, setYearly] = useState(false);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);

  /* Currency is guessed from the browser locale then remembered. Deliberately
     not IP geolocation: it costs a request, is wrong behind VPNs, and cannot
     run at all in prerendered output. The visible toggle is the real control. */
  useEffect(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    setCurrency(saved && CURRENCIES[saved] ? saved : guessCurrency(DEFAULT_CURRENCY));
  }, []);

  const pickCurrency = (code) => {
    setCurrency(code);
    window.localStorage.setItem(STORAGE_KEY, code);
  };

  return (
    <>
      <section id="pricing" className="scroll-mt-24 bg-deep pt-16 pb-16 text-center lg:pt-20">
        <div className="wrap">
          <Eyebrow className="mb-3 !text-amber">Pricing</Eyebrow>
          <h2 className="mb-4 font-display text-[28px] font-bold text-white lg:text-[34px]">
            <AccentedHeading text="Priced by the size of your flock." accent="your flock" />
          </h2>
          <p className="mx-auto mb-9 max-w-[520px] text-[15.5px] text-mist">
            Pay monthly, or yearly and get two months free. Sensors are a separate one-time
            hardware cost, so you can start on records alone and add monitoring later.
          </p>

          {/* Both toggles only change displayed amounts, so each is hidden
              unless it has something to switch between — a control that
              visibly does nothing reads as a broken page. */}
          {PRICES_APPROVED && (YEARLY_BILLING_ENABLED || SHOW_CURRENCY_PICKER) && (
            <div className="flex flex-col items-center gap-4">
              {YEARLY_BILLING_ENABLED && <BillingToggle yearly={yearly} onChange={setYearly} />}
              {SHOW_CURRENCY_PICKER && (
                <CurrencyToggle value={currency} onChange={pickCurrency} />
              )}
            </div>
          )}
        </div>
      </section>

      {/* Pulled up over the dark band above. relative/z-10 so the cards win the
          stacking order against that section's background. */}
      <section className="relative z-10 -mt-10 pb-4">
        <div className="wrap">
          <div className="grid items-start gap-5 lg:grid-cols-3">
            {TIERS.map((tier) => (
              <TierCard key={tier.id} tier={tier} yearly={yearly} currency={currency} />
            ))}
          </div>

          <IncludedEverywhere />
          <SensorsPanel />
        </div>
      </section>

      <Faq />
    </>
  );
}

function BillingToggle({ yearly, onChange }) {
  const saving = yearlySavingPercent();
  return (
    <div
      role="radiogroup"
      aria-label="Billing period"
      className="inline-flex items-center rounded-full border border-white/20 bg-white/10 p-1"
    >
      {[
        { label: 'Monthly', value: false },
        { label: 'Yearly', value: true },
      ].map((opt) => (
        <button
          key={opt.label}
          type="button"
          role="radio"
          aria-checked={yearly === opt.value}
          onClick={() => onChange(opt.value)}
          className={[
            'rounded-full px-5 py-2 text-[13.5px] font-semibold transition',
            yearly === opt.value ? 'bg-amber text-deep' : 'text-mist hover:text-white',
          ].join(' ')}
        >
          {opt.label}
          {opt.value && (
            <span className={yearly ? 'text-deep/70' : 'text-amber'}> · save {saving}%</span>
          )}
        </button>
      ))}
    </div>
  );
}

function CurrencyToggle({ value, onChange }) {
  return (
    <div
      role="radiogroup"
      aria-label="Currency"
      className="inline-flex items-center gap-1 text-[13px]"
    >
      {Object.values(CURRENCIES).map((c) => (
        <button
          key={c.code}
          type="button"
          role="radio"
          aria-checked={value === c.code}
          onClick={() => onChange(c.code)}
          className={[
            'rounded-full px-3.5 py-1.5 font-mono uppercase tracking-wide transition',
            value === c.code ? 'bg-white/15 text-white' : 'text-sage hover:text-white',
          ].join(' ')}
        >
          {c.code}
          <span className="sr-only">, {c.country}</span>
        </button>
      ))}
    </div>
  );
}

function TierCard({ tier, yearly, currency }) {
  const monthly = tier.monthly?.[currency] ?? null;
  const shown = monthly == null ? null : yearly ? yearlyPerMonth(monthly) : monthly;
  const custom = tier.monthly == null;

  return (
    <div
      className={[
        'flex h-full flex-col rounded-2xl border bg-white p-6 sm:p-7',
        tier.featured ? 'border-amber shadow-card ring-1 ring-amber' : 'border-line',
      ].join(' ')}
    >
      <div className="mb-5">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="font-display text-[19px] font-bold text-deep">{tier.name}</h3>
          {tier.featured && (
            <span className="rounded-full bg-amber px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-deep">
              Popular
            </span>
          )}
        </div>
        <p className="text-[13.5px] text-muted">{tier.blurb}</p>
      </div>

      <div className="mb-5 border-b border-line pb-5">
        {custom || !PRICES_APPROVED ? (
          <>
            <p className="font-display text-[28px] font-bold text-deep">
              {custom ? 'Custom' : 'On request'}
            </p>
            {(!custom || tier.quoteNote) && (
              <p className="mt-1.5 text-[12.5px] text-muted">
                {custom ? tier.quoteNote : 'Priced on flock size, we quote on the call.'}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="font-display text-[30px] font-bold leading-none text-deep">
              {formatPrice(shown, currency)}
              <span className="ml-1 align-middle text-[13px] font-medium text-muted">/month</span>
            </p>
            <p className="mt-1.5 text-[12.5px] text-muted">
              {yearly
                ? `${formatPrice(yearlyTotal(monthly), currency)} billed yearly, 2 months free`
                : 'Billed monthly'}
            </p>
          </>
        )}
      </div>

      <dl className="mb-5 space-y-1.5 text-[13px]">
        <div className="flex justify-between gap-2">
          <dt className="text-muted">Birds</dt>
          <dd className="font-semibold text-deep">{tier.birdCap}</dd>
        </div>
        {tier.users && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted">Team</dt>
            <dd className="font-semibold text-deep">{tier.users}</dd>
          </div>
        )}
      </dl>

      <ul className="mb-7 flex-1 space-y-2.5">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13.5px] text-ink-soft">
            <CheckIcon />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button to="/demo" variant={tier.featured ? 'primary' : 'dark'} className="w-full">
        {custom ? 'Get a quote' : 'Book a demo'}
      </Button>
    </div>
  );
}

/**
 * The platform basics every plan gets, said once instead of padding each card.
 * Keeps the cards about what differs, which is what someone comparing them
 * is actually reading for.
 */
function IncludedEverywhere() {
  return (
    <div className="mt-6 rounded-2xl border border-line bg-cream px-5 py-5 text-center sm:px-7">
      <p className="mb-3 font-mono text-[10.5px] uppercase tracking-[0.15em] text-muted">
        Every plan includes
      </p>
      <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2.5">
        {INCLUDED_EVERYWHERE.map((item) => (
          <li key={item} className="flex items-center gap-2 text-[13.5px] text-ink-soft">
            <CheckIcon />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The sensor add-on. Dark so it reads as a separate offer rather than a fourth
 * plan, with one tile per reading because farmers buy the reading, not the
 * device.
 */
function SensorsPanel() {
  return (
    <div className="mt-6 rounded-2xl bg-deep px-5 py-7 text-left sm:px-8 sm:py-8">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="mb-2 inline-block rounded-full bg-amber px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-deep">
            Add-on · any plan
          </span>
          <h3 className="font-display text-[20px] font-bold text-white lg:text-[22px]">
            {SENSORS.heading}
          </h3>
        </div>
        <p className="max-w-[360px] text-[13.5px] text-mist">{SENSORS.sub}</p>
      </div>

      <ul className="mb-6 grid gap-3 sm:grid-cols-3">
        {SENSORS.readings.map((r, i) => (
          <li key={r.name} className="rounded-xl border border-white/10 bg-white/5 p-4">
            <span className="mb-3 grid h-9 w-9 place-items-center rounded-lg bg-amber/15 text-amber">
              <SensorIcon index={i} />
            </span>
            <p className="font-display text-[15px] font-bold text-white">{r.name}</p>
            <p className="mt-1 text-[13px] text-mist">{r.note}</p>
          </li>
        ))}
      </ul>

      <ul className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-x-6">
        {SENSORS.points.map((p) => (
          <li key={p} className="flex items-start gap-2 text-[13.5px] text-white/90">
            <CheckIcon className="text-amber" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Thermometer, air, water drop — in the order of SENSORS.readings. */
const SENSOR_PATHS = [
  'M10 13.5V4a2 2 0 1 1 4 0v9.5a4 4 0 1 1-4 0Z M12 17v-6',
  'M3 8h10a3 3 0 1 0-3-3 M3 12h15a3 3 0 1 1-3 3 M3 16h7',
  'M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11Z',
];

function SensorIcon({ index }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[18px] w-[18px]"
      aria-hidden="true"
    >
      <path d={SENSOR_PATHS[index]} />
    </svg>
  );
}

/**
 * FAQ as a disclosure list.
 *
 * One panel open at a time. Four answers stacked open push the page down by
 * roughly a screen and a half on a phone, which buries whatever follows; the
 * closed state lets someone scan all four questions at once and open only the
 * one they care about.
 *
 * Built from real <button> elements rather than clickable divs, so it works by
 * keyboard and announces itself: aria-expanded reports the state, aria-controls
 * ties the trigger to its panel, and the panel is hidden from the accessibility
 * tree when closed rather than merely being invisible.
 *
 * The open/close animation uses a grid whose row track moves between 0fr and
 * 1fr. That transitions to the content's natural height without measuring it in
 * JavaScript, which is what makes max-height accordions either clip long
 * answers or bounce.
 */
function Faq() {
  const [open, setOpen] = useState(null);

  return (
    <section className="pt-14 pb-16 lg:pt-16 lg:pb-20">
      <div className="wrap max-w-[760px]">
        <h3 className="mb-7 text-center font-display text-[22px] font-bold text-deep lg:text-[26px]">
          Questions farms ask (FAQ)
        </h3>

        <div className="divide-y divide-line border-y border-line">
          {FAQ.map((item, i) => {
            const isOpen = open === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-trigger-${i}`;

            return (
              <div key={item.q}>
                <h4>
                  <button
                    type="button"
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpen(isOpen ? null : i)}
                    className="flex w-full items-start justify-between gap-4 py-5 text-left transition-colors hover:text-field"
                  >
                    <span className="font-display text-[15px] font-bold text-deep">{item.q}</span>

                    {/* 40px tap target, comfortably over the 44px-with-padding
                        minimum once the row's own padding is counted. */}
                    <span
                      className={[
                        'mt-[-2px] grid h-9 w-9 flex-none place-items-center rounded-full border transition-all duration-200',
                        'motion-reduce:transition-none',
                        isOpen
                          ? 'rotate-180 border-field bg-field text-white'
                          : 'border-line bg-white text-muted',
                      ].join(' ')}
                    >
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                        aria-hidden="true"
                      >
                        <path d="M5 7.5l5 5 5-5" />
                      </svg>
                    </span>
                  </button>
                </h4>

                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className={[
                    'grid transition-all duration-300 ease-out motion-reduce:transition-none',
                    // `invisible` rather than the hidden attribute: display:none
                    // would cancel the row-track animation, while visibility
                    // still takes the panel out of the accessibility tree and
                    // stops anything inside receiving focus.
                    isOpen
                      ? 'visible grid-rows-[1fr] opacity-100'
                      : 'invisible grid-rows-[0fr] opacity-0',
                  ].join(' ')}
                >
                  <div className="overflow-hidden">
                    <p className="pb-5 pr-12 text-[14px] leading-relaxed text-muted">{item.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
